import * as React from 'react';
import { connect } from 'react-redux';

import PageSection from 'components/page/page-section/PageSection';
import Sidenote from 'components/sidenote/Sidenote';
import WarningBar from './WarningBar';
import { withModal } from 'components/modal/ModalHOC';

import OrganizationSettings from '../organization-settings/OrganizationSettings';
import BusinessDetailsExplanationModal from '../settings/influencer-explanation-modals/business-details-explanation-modal/BusinessDetailsExplanationModal';

import { fetchOrganizationSettings } from '../../reducers/organizationSettings';

import {
  ORGANIZATION_TYPE_INFLUENCER,
  ORGANIZATION_TYPE_INFLUENCERS_AGENCY,
  getPaymentDetailsExplanationType,
  isBrandsFocusedOrganization,
  shouldInfluencerFillBankDetails,
  shouldInfluencerFillBusinessDetails
} from 'utils/organization';
import { __t, applyTranslations } from 'utils/language';

import './organization-billing.scss';

applyTranslations(require('./organization-billing.i18n.json'));

import type { Organization, OrganizationSettings as OrganizationSettingsAlias } from 'types';

type OwnProps = {
  currentOrganization: Organization,
  organizationType?: string
};

type MissingDetails = {
  isBusinessDetailsMissing: boolean,
  isBankDetailsMissing: boolean
};

type StateProps = {
  organizationSettings: OrganizationSettingsAlias,
  organizationIsLoading: boolean
};

type DispatchProps = {
  dispatchFetchOrganizationSettings: (a: number) => Promise<void>
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  activeForm: string | undefined | null,
  country: string | null
};

const msgKey = 'organization_billing';
const ActionLinkWithModal = withModal(
  <span className="organization-billing__learn-more">
    {__t(`${msgKey}.learn_more`)}
  </span>, {
    modalContent: BusinessDetailsExplanationModal
  });

class OrganizationBilling extends React.PureComponent<Props, State> {
  static defaultProps = {
    organizationType: 'brand'
  };

  state: State = {
    activeForm: null,
    country: null
  };

  componentDidMount() {
    const {
      dispatchFetchOrganizationSettings,
      currentOrganization,
      organizationIsLoading
    } = this.props;
    const orgId = currentOrganization && currentOrganization.id;

    if (orgId && !organizationIsLoading) {
      dispatchFetchOrganizationSettings(orgId);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { dispatchFetchOrganizationSettings, currentOrganization } = this.props;
    const orgId = currentOrganization && currentOrganization.id;
    const prevOrgId = prevProps.currentOrganization && prevProps.currentOrganization.id;

    if (orgId && prevOrgId && orgId !== prevOrgId) {
      dispatchFetchOrganizationSettings(orgId);
    }
  }

  handleModeSwitch = (activeForm?: string | null): void => {
    const { organizationSettings } = this.props;
    this.setState({
      activeForm,
      country: organizationSettings.billingAddress?.country
    });
  };

  handleLocationInput = (country: string) => {
    this.setState(prevState => ({
      ...prevState,
      country
    }));
  };

  isRegisteredBusiness = () => {
    const { organizationSettings } = this.props;
    const businessDetails = organizationSettings.businessDetails ?? null;

    return businessDetails?.registeredBusiness === true
      || businessDetails?.registeredBusiness === 'true';
  };

  getMissingDetailsStates(): MissingDetails {
    const { organizationSettings } = this.props;

    return {
      isBankDetailsMissing: shouldInfluencerFillBankDetails(organizationSettings),
      isBusinessDetailsMissing: shouldInfluencerFillBusinessDetails(organizationSettings)
    };
  }

  getCountryType(): string | null {
    const { organizationSettings } = this.props;
    const country = this.state.country ?? organizationSettings.billingAddress?.country;

    return getPaymentDetailsExplanationType(country);
  }

  getRenderingKey(): string {
    const { currentOrganization, organizationSettings } = this.props;

    return `${JSON.stringify(organizationSettings)}-${currentOrganization
      ? currentOrganization.id
      : '0'}`;
  }

  render() {
    const { currentOrganization, organizationSettings, organizationType, organizationIsLoading } = this.props;
    const { activeForm } = this.state;
    const isBrand = isBrandsFocusedOrganization(currentOrganization);
    const key = this.getRenderingKey();

    const {
      isBankDetailsMissing,
      isBusinessDetailsMissing
    } = this.getMissingDetailsStates();

    const missing_details_str = isBankDetailsMissing ? 'bank' : 'business';

    const isInfluencersAgency = currentOrganization?.type === ORGANIZATION_TYPE_INFLUENCERS_AGENCY;
    const isInfluencers = currentOrganization?.type === ORGANIZATION_TYPE_INFLUENCER;

    const renderPaymentDetailsInfo = () => (
      <React.Fragment>
        {isInfluencersAgency &&
          <span className="organization-billing__influencers-agency-note">
            {__t(`${msgKey}.influencers_agency_note`)}
          </span>
        }
        {!isInfluencersAgency && !isBrand && !organizationIsLoading && (isBankDetailsMissing || isBusinessDetailsMissing) && (
          <Sidenote
            withCenteredIcon
            type="info"
            layout="horizontal"
            className="organization-billing__sidenote"
          >
            <div>
              {__t(`${msgKey}.missing_bank_business_details_warning`, {
                str: isBankDetailsMissing && isBusinessDetailsMissing ? 'business and bank' : missing_details_str
              })}
              <ActionLinkWithModal />
            </div>
          </Sidenote>
        )}
        {!isBrand && isBusinessDetailsMissing && !organizationIsLoading && (
          <WarningBar text={__t(`${msgKey}.missing_business_details_warning`)} />
        )}
      </React.Fragment>
    );

    return (
      currentOrganization && (
        <div className="organization-billing__container">
          <PageSection
            title={__t(
              isBrand
                ? `${msgKey}.section_title.${organizationType || 'brand'}_details`
                : `${msgKey}.section_title.business_details`
            )}
            margin="none"
            titleProps={{ margin: 'top-none' }}
            afterTitle={renderPaymentDetailsInfo()}
          >
            <OrganizationSettings
              key={`details-${key}`}
              currentOrganization={currentOrganization}
              organizationSettings={organizationSettings}
              settingsGroup={isBrand ? 'brandBusinessDetails' : 'influencerBusinessDetails'}
              organizationType={organizationType}
              isDisabled={
                Boolean(
                  activeForm && activeForm !== 'brandBusinessDetails'
                  && activeForm !== 'influencerBusinessDetails'
                ) || Boolean(currentOrganization.agencyOrganizationId && isInfluencers)
              }
              onModeSwitch={this.handleModeSwitch}
              onLocationInput={this.handleLocationInput}
            />
          </PageSection>
          {
            isBrand && (
              <PageSection title={__t(`${msgKey}.section_title.billing_address`)}>
                <OrganizationSettings
                  key={`brand-${key}`}
                  currentOrganization={currentOrganization}
                  organizationSettings={organizationSettings}
                  settingsGroup="billingAddress"
                  organizationType={organizationType}
                  isDisabled={Boolean(activeForm && activeForm !== 'billingAddress') || Boolean(currentOrganization.agencyOrganizationId && isInfluencers)}
                  onModeSwitch={this.handleModeSwitch}
                />
              </PageSection>
            )
          }
          {
            !isBrand && (
              <PageSection
                title={__t(`${msgKey}.section_title.bank_details`)}
                afterTitle={isBankDetailsMissing && !organizationIsLoading &&
                  <WarningBar text={__t(`${msgKey}.missing_bank_details_warning`)} />
                }
              >
                <OrganizationSettings
                  key={`influencer-${key}`}
                  currentOrganization={currentOrganization}
                  organizationSettings={organizationSettings}
                  organizationType={organizationType}
                  settingsGroup="bankDetails"
                  isDisabled={Boolean(activeForm && activeForm !== 'bankDetails') || Boolean(currentOrganization.agencyOrganizationId && isInfluencers)}
                  onModeSwitch={this.handleModeSwitch}
                />
              </PageSection>
            )
          }
        </div>
      )
    );
  }
}

const mapStateToProps = ({ organizationSettings }): StateProps => ({
  organizationSettings: organizationSettings.organization,
  organizationIsLoading: organizationSettings.isLoading
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchOrganizationSettings: (
    organizationId
  ) => dispatch(fetchOrganizationSettings(organizationId))
});

export default connect(mapStateToProps, mapDispatchToProps)(OrganizationBilling);
